/**
 * Moon What's Popular Component - JavaScript
 * Handles carousel navigation, autoplay, and interactions
 */

class PopularCarousel {
    constructor(carouselElement, options = {}) {
        this.carousel = carouselElement;
        this.wrapper = carouselElement.closest('.popular-carousel-wrapper');
        this.prevBtn = this.wrapper?.querySelector('.carousel-btn-prev');
        this.nextBtn = this.wrapper?.querySelector('.carousel-btn-next');
        this.scrollAmount = 350; // Pixels to scroll per click
        this.autoplayInterval = null;
        this.autoplayEnabled = options.autoplay || false;
        this.autoplaySpeed = (options.autoplaySpeed || 5) * 1000; // Convert to milliseconds

        this.init();
    }

    init() {
        if (!this.carousel) {
            console.warn('Carousel element not found');
            return;
        }

        // Add event listeners
        this.prevBtn?.addEventListener('click', () => this.scrollPrev());
        this.nextBtn?.addEventListener('click', () => this.scrollNext());

        // Update button states on scroll
        this.carousel.addEventListener('scroll', () => this.updateButtonStates());

        // Update button states on resize
        window.addEventListener('resize', () => this.updateButtonStates());

        // Initial button state update
        this.updateButtonStates();

        // Add keyboard navigation
        this.addKeyboardNavigation();

        // Add touch/swipe support
        this.addTouchSupport();

        // Start autoplay if enabled
        if (this.autoplayEnabled) {
            this.startAutoplay();
            
            // Pause on hover
            this.carousel.addEventListener('mouseenter', () => this.pauseAutoplay());
            this.carousel.addEventListener('mouseleave', () => this.resumeAutoplay());
        }
    }

    scrollPrev() {
        this.carousel.scrollBy({
            left: -this.scrollAmount,
            behavior: 'smooth'
        });
    }

    scrollNext() {
        this.carousel.scrollBy({
            left: this.scrollAmount,
            behavior: 'smooth'
        });
    }

    updateButtonStates() {
        if (!this.carousel || !this.prevBtn || !this.nextBtn) return;

        const scrollLeft = this.carousel.scrollLeft;
        const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth;

        // Disable/enable previous button
        if (scrollLeft <= 0) {
            this.prevBtn.disabled = true;
        } else {
            this.prevBtn.disabled = false;
        }

        // Disable/enable next button
        if (scrollLeft >= maxScroll - 1) {
            this.nextBtn.disabled = true;
        } else {
            this.nextBtn.disabled = false;
        }
    }

    addKeyboardNavigation() {
        this.carousel.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.scrollPrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.scrollNext();
            }
        });
    }

    addTouchSupport() {
        let startX = 0;
        let scrollLeft = 0;
        let isDown = false;

        this.carousel.addEventListener('mousedown', (e) => {
            isDown = true;
            this.carousel.style.cursor = 'grabbing';
            startX = e.pageX - this.carousel.offsetLeft;
            scrollLeft = this.carousel.scrollLeft;
        });

        this.carousel.addEventListener('mouseleave', () => {
            isDown = false;
            this.carousel.style.cursor = 'grab';
        });

        this.carousel.addEventListener('mouseup', () => {
            isDown = false;
            this.carousel.style.cursor = 'grab';
        });

        this.carousel.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - this.carousel.offsetLeft;
            const walk = (x - startX) * 2;
            this.carousel.scrollLeft = scrollLeft - walk;
        });

        // Removed custom touch events to allow native horizontal scroll/swipe behavior on mobile
    }

    // Autoplay functionality
    startAutoplay() {
        if (!this.autoplayEnabled) return;
        
        this.autoplayInterval = setInterval(() => {
            const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth;
            const currentScroll = this.carousel.scrollLeft;

            // If at the end, scroll back to start
            if (currentScroll >= maxScroll - 1) {
                this.carousel.scrollTo({
                    left: 0,
                    behavior: 'smooth'
                });
            } else {
                this.scrollNext();
            }
        }, this.autoplaySpeed);
    }

    pauseAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    resumeAutoplay() {
        if (this.autoplayEnabled && !this.autoplayInterval) {
            this.startAutoplay();
        }
    }

    stopAutoplay() {
        this.pauseAutoplay();
        this.autoplayEnabled = false;
    }

    // Public method to scroll to a specific card
    scrollToCard(index) {
        const cards = this.carousel.querySelectorAll('.popular-card');
        if (cards[index]) {
            cards[index].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            });
        }
    }
}

// Initialize all carousels on the page
function initializeCarousels() {
    const carousels = document.querySelectorAll('.moon-whats-popular-section .popular-carousel');
    const instances = [];

    carousels.forEach(carousel => {
        // Get settings from section
        const section = carousel.closest('.moon-whats-popular-wrapper');
        const sectionId = carousel.id.replace('popularCarousel-', '');
        
        // Check if autoplay is enabled (you can pass this via data attributes)
        const autoplay = carousel.dataset.autoplay === 'true';
        const autoplaySpeed = parseInt(carousel.dataset.autoplaySpeed) || 5;

        const instance = new PopularCarousel(carousel, {
            autoplay: autoplay,
            autoplaySpeed: autoplaySpeed
        });
        instances.push(instance);
    });

    return instances;
}

// Analytics tracking
function trackItemClick(itemName) {
    if (typeof window.ShopifyAnalytics !== 'undefined') {
        window.ShopifyAnalytics.lib.track('Popular Item Clicked', {
            item: itemName
        });
    }
    console.log(`Popular item clicked: ${itemName}`);
}

// Add click tracking
function initializeAnalytics() {
    const cards = document.querySelectorAll('.moon-whats-popular-section .popular-card-link');
    cards.forEach(card => {
        card.addEventListener('click', (e) => {
            const itemName = card.querySelector('.popular-card-heading')?.textContent;
            if (itemName) {
                trackItemClick(itemName);
            }
        });
    });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

function init() {
    // Initialize all carousels
    const carouselInstances = initializeCarousels();

    // Initialize analytics tracking
    initializeAnalytics();

    // Make carousel instances globally available for debugging
    window.popularCarousels = carouselInstances;
}

// Shopify section events - reinitialize when sections are loaded/reloaded
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
        if (event.target.querySelector('.moon-whats-popular-section')) {
            init();
        }
    });

    document.addEventListener('shopify:section:reorder', function(event) {
        if (event.target.querySelector('.moon-whats-popular-section')) {
            init();
        }
    });
}

// Export for module usage
export { PopularCarousel, initializeCarousels };
